import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Card, CardContent, CardHeader } from "@learn-me/ui";
import { api } from "@/lib/api";
import {
  ArrowLeft,
  Save,
  X,
  CheckCircle2,
  Eye,
  FileText,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import type { ApiResponse } from "@learn-me/types";
import { z } from "zod";
import { ChapterAITools } from "@/features/admin/components/ChapterAITools";
import { ChapterAudioTools } from "@/features/admin/components/ChapterAudioTools";
import { QuizPreview } from "@/features/admin/components/QuizPreview";
import { SectionEditor } from "@/features/admin/components/SectionEditor";
import { adminApi, type GeneratedQuiz } from "@/features/admin/api/adminApi";
import { RichTextEditor, ContentPreview } from "@/components/editor";
import ReactPlayer from "react-player";

// Helper function to format duration for display
function formatDurationDisplay(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

// Form validation schema
const chapterFormSchema = z.object({
  title: z
    .string()
    .min(1, "El título es requerido")
    .max(200, "El título debe tener menos de 200 caracteres"),
  description: z.string().max(1000, "La descripción es muy larga").optional(),
  content: z.string().optional(),
  videoUrl: z
    .string()
    .url("URL de video inválida")
    .optional()
    .or(z.literal("")),
  videoDuration: z.number().min(0).optional().nullable(),
  position: z.number().int().min(1).optional(),
  isFree: z.boolean(),
  isPublished: z.boolean(),
  requiresQuizPass: z.boolean(),
  minQuizScore: z.number().min(0).max(100),
});

type ChapterFormData = z.infer<typeof chapterFormSchema>;

interface Chapter {
  id: string;
  title: string;
  description?: string;
  content?: string;
  videoUrl?: string;
  videoDuration?: number;
  position: number;
  isFree: boolean;
  isPublished: boolean;
  requiresQuizPass: boolean;
  minQuizScore: number;
  courseId: string;
  // Audio TTS
  audioUrl?: string;
  audioPublicId?: string;
  audioDuration?: number;
  audioVoice?: string;
  audioGeneratedAt?: string;
}

export default function ChapterEditorPage() {
  const { courseId, id } = useParams<{ courseId: string; id?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [generatedQuiz, setGeneratedQuiz] = useState<GeneratedQuiz | null>(
    null,
  );
  const [showPreview, setShowPreview] = useState(false);

  const {
    data: chapter,
    isLoading,
    refetch: refetchChapter,
  } = useQuery({
    queryKey: ["chapter-edit", id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Chapter>>(`/chapters/${id}`);
      return response.data.data;
    },
    enabled: isEditing,
  });

  // Fetch sections to detect legacy mode
  const { data: sections = [], refetch: refetchSections } = useQuery({
    queryKey: ["chapter-sections", id],
    queryFn: () => adminApi.getChapterSections(id!),
    enabled: isEditing && !!id,
  });

  // Determine if this is a legacy chapter (has content but no sections)
  const hasLegacyContent = !!(
    chapter?.content && chapter.content.trim().length > 0
  );
  const hasSections = sections.length > 0;
  const isLegacyMode = hasLegacyContent && !hasSections;

  const handleAudioGenerated = useCallback(() => {
    refetchChapter();
  }, [refetchChapter]);

  // Parse legacy content to sections
  const parseSectionsMutation = useMutation({
    mutationFn: () => adminApi.parseContentToSections(id!),
    onSuccess: () => {
      refetchSections();
      queryClient.invalidateQueries({ queryKey: ["chapter-sections", id] });
      toast.success("Contenido parseado en secciones exitosamente");
    },
    onError: () => {
      toast.error("Error al parsear contenido");
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
    control,
  } = useForm<ChapterFormData>({
    resolver: zodResolver(chapterFormSchema),
    defaultValues: {
      isFree: false,
      isPublished: false,
      requiresQuizPass: false,
      minQuizScore: 70,
    },
  });

  const requiresQuizPass = watch("requiresQuizPass");
  const currentContent = watch("content");
  const videoUrl = watch("videoUrl");
  const videoDuration = watch("videoDuration");

  // Handle video duration detection from ReactPlayer
  const handleDurationDetected = useCallback(
    (duration: number) => {
      const seconds = Math.round(duration);
      setValue("videoDuration", seconds, { shouldDirty: true });
    },
    [setValue],
  );

  useEffect(() => {
    if (chapter) {
      reset({
        title: chapter.title,
        description: chapter.description || "",
        content: chapter.content || "",
        videoUrl: chapter.videoUrl || "",
        videoDuration: chapter.videoDuration || undefined,
        position: chapter.position,
        isFree: chapter.isFree,
        isPublished: chapter.isPublished,
        requiresQuizPass: chapter.requiresQuizPass ?? false,
        minQuizScore: chapter.minQuizScore ?? 70,
      });
    }
  }, [chapter, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: ChapterFormData) => {
      const response = await api.post<ApiResponse<Chapter>>("/chapters", {
        ...data,
        courseId,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-edit", courseId] });
      toast.success("Capítulo creado exitosamente");
      navigate(`/admin/courses/${courseId}`);
    },
    onError: (
      error: Error & { response?: { data?: { error?: { message?: string } } } },
    ) => {
      const message =
        error.response?.data?.error?.message || "Error al crear el capítulo";
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ChapterFormData) => {
      const response = await api.put<ApiResponse<Chapter>>(
        `/chapters/${id}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-edit", courseId] });
      queryClient.invalidateQueries({ queryKey: ["chapter-edit", id] });
      toast.success("Capítulo actualizado exitosamente");
    },
    onError: (
      error: Error & { response?: { data?: { error?: { message?: string } } } },
    ) => {
      const message =
        error.response?.data?.error?.message ||
        "Error al actualizar el capítulo";
      toast.error(message);
    },
  });

  const onSubmit = (data: ChapterFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleContentGenerated = (content: string) => {
    setValue("content", content, { shouldDirty: true });
  };

  const handleQuizGenerated = (quiz: GeneratedQuiz) => {
    setGeneratedQuiz(quiz);
  };

  const handleSaveQuiz = async () => {
    if (!generatedQuiz || !id) return;

    try {
      await api.post("/quizzes", {
        chapterId: id,
        title: generatedQuiz.title,
        passingScore: watch("minQuizScore") || 70,
        questions: generatedQuiz.questions.map((q, idx) => ({
          text: q.text,
          type: "SINGLE_CHOICE",
          explanation: q.explanation,
          points: 1,
          order: idx + 1,
          options: q.options,
        })),
      });

      toast.success("Quiz guardado exitosamente");
      setGeneratedQuiz(null);
      queryClient.invalidateQueries({ queryKey: ["chapter-edit", id] });
    } catch {
      toast.error("Error al guardar el quiz");
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-muted rounded w-1/4" />
        <div className="h-64 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/admin/courses/${courseId}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al curso
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">
            {isEditing ? "Editar Capítulo" : "Nuevo Capítulo"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {isEditing && (
            <Link to={`/admin/courses/${courseId}/chapters/${id}/edit`}>
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-2" />
                Editor WYSIWYG
              </Button>
            </Link>
          )}
          {isDirty && (
            <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              Cambios sin guardar
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">
                Información del Capítulo
              </h2>
            </CardHeader>
            <CardContent>
              <form
                id="chapter-form"
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <Input
                  label="Título *"
                  placeholder="Ej: Introducción al tema"
                  error={errors.title?.message}
                  {...register("title")}
                />

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Descripción
                  </label>
                  <textarea
                    className={`w-full px-3 py-2 border rounded-lg min-h-[80px] ${
                      errors.description ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Breve descripción del capítulo..."
                    {...register("description")}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <Input
                  label="URL de Video (YouTube/Vimeo)"
                  placeholder="https://www.youtube.com/watch?v=..."
                  error={errors.videoUrl?.message}
                  {...register("videoUrl")}
                />

                {/* Video Preview with Auto Duration Detection */}
                {videoUrl && ReactPlayer.canPlay(videoUrl) && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      Vista Previa del Video
                    </label>
                    <div className="aspect-video rounded-lg overflow-hidden bg-black">
                      <ReactPlayer
                        url={videoUrl}
                        width="100%"
                        height="100%"
                        controls
                        light
                        onDuration={handleDurationDetected}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Input
                    type="number"
                    label="Duracion del Video (segundos)"
                    placeholder="Se detecta automaticamente"
                    error={errors.videoDuration?.message}
                    {...register("videoDuration", { valueAsNumber: true })}
                  />
                  {videoDuration && videoDuration > 0 && (
                    <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {formatDurationDisplay(videoDuration)}
                    </p>
                  )}
                </div>

                {/* Content Editor - Only show for new chapters or legacy mode */}
                {(!isEditing || isLegacyMode) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">
                        Contenido del Capitulo
                      </label>
                      {isLegacyMode && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          Modo Legado
                        </span>
                      )}
                    </div>
                    <Controller
                      name="content"
                      control={control}
                      render={({ field }) => (
                        <RichTextEditor
                          content={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Escribe el contenido del capitulo aqui..."
                        />
                      )}
                    />
                    {isLegacyMode && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <FileText className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-amber-800 font-medium">
                              Este capitulo usa el formato antiguo
                            </p>
                            <p className="text-xs text-amber-600 mt-1">
                              Parsea el contenido en secciones para habilitar
                              audio por seccion y mejor navegacion.
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={() => parseSectionsMutation.mutate()}
                              disabled={parseSectionsMutation.isPending}
                            >
                              {parseSectionsMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <FileText className="h-4 w-4 mr-1" />
                              )}
                              Convertir a Secciones
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {!isLegacyMode && (
                      <p className="text-xs text-gray-500 mt-2">
                        Usa la barra de herramientas para formatear texto,
                        agregar imagenes y enlaces.
                      </p>
                    )}
                  </div>
                )}

                {/* Section-based editing message - Only show for chapters with sections */}
                {isEditing && hasSections && (
                  <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-primary-800 font-medium">
                          Contenido gestionado por secciones
                        </p>
                        <p className="text-xs text-primary-600 mt-1">
                          Edita el contenido en la seccion &quot;Secciones y
                          Audio&quot; mas abajo. Cada seccion puede tener su
                          propio audio generado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Input
                  type="number"
                  label="Posición"
                  placeholder="1"
                  {...register("position", { valueAsNumber: true })}
                />

                <div className="flex items-center gap-6 py-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      {...register("isFree")}
                    />
                    <span className="text-sm">Vista previa gratuita</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      {...register("isPublished")}
                    />
                    <span className="text-sm">Publicado</span>
                  </label>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Quiz Settings */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Configuración de Quiz</h2>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                  {...register("requiresQuizPass")}
                />
                <div>
                  <span className="text-sm font-medium">
                    Requiere aprobar quiz para continuar
                  </span>
                  <p className="text-xs text-gray-500">
                    Los estudiantes deben aprobar el quiz antes de avanzar al
                    siguiente capítulo.
                  </p>
                </div>
              </label>

              {requiresQuizPass && (
                <div className="ml-7 space-y-2">
                  <label className="block text-sm font-medium">
                    Puntaje mínimo para aprobar: {watch("minQuizScore")}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    {...register("minQuizScore", { valueAsNumber: true })}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generated Quiz Preview */}
          {generatedQuiz && (
            <QuizPreview
              quiz={generatedQuiz}
              onSave={handleSaveQuiz}
              onDiscard={() => setGeneratedQuiz(null)}
              isSaving={false}
            />
          )}

          {/* Section Editor - Only when editing */}
          {isEditing && id && (
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Secciones y Audio</h2>
                <p className="text-sm text-gray-500">
                  Gestiona las secciones del capitulo y genera audio individual
                  para cada una
                </p>
              </CardHeader>
              <CardContent>
                <SectionEditor chapterId={id} courseId={courseId} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Actions Card */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Acciones</h2>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                type="submit"
                form="chapter-form"
                className="w-full"
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? "Guardar Cambios" : "Crear Capítulo"}
              </Button>
              {currentContent && currentContent.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowPreview(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Vista Previa
                </Button>
              )}
              <Link to={`/admin/courses/${courseId}`} className="block">
                <Button variant="outline" className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* AI Tools - Only for editing */}
          {isEditing && courseId && (
            <ChapterAITools
              chapterId={id}
              courseId={courseId}
              hasSections={hasSections}
              onSectionsGenerated={() => refetchSections()}
            />
          )}

          {/* Audio Tools - Only for editing with content */}
          {isEditing && id && currentContent && currentContent.length > 0 && (
            <ChapterAudioTools
              chapterId={id}
              hasContent={true}
              audioUrl={chapter?.audioUrl}
              audioDuration={chapter?.audioDuration}
              audioVoice={chapter?.audioVoice}
              onAudioGenerated={handleAudioGenerated}
            />
          )}
        </div>
      </div>

      {/* Content Preview Modal */}
      <ContentPreview
        content={currentContent || ""}
        title={watch("title") || "Vista Previa"}
        audioUrl={chapter?.audioUrl}
        audioDuration={chapter?.audioDuration}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
}
